import { NavItem, NavListItemWithBlockDescription, Select, SelectProps } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { LocaleIdentifier } from '../../../../types/localeIdentifier'
import { getCurrentLocale } from '../../../../utils'
import { TaxRateGroup } from '../../types/taxRateGroup'
import { VatRate } from '../../types/vatRate'
import { VatRateItemValue } from './types/vatRateItemValue'
import { getDisplayedVatRate } from './utils/getDisplayedVatRate'
import { getItems } from './utils/getItems'

export type HandleVatRateSelect = (id?: string, value?: VatRateItemValue) => void

interface VatRateSelectProps extends Omit<SelectProps<VatRateItemValue>, 'items' | 'dropdownItemRender' | 'onSelect'> {
  locale?: LocaleIdentifier
  onSelect?: HandleVatRateSelect
  region?: TaxRateGroup
  vatRates: VatRate[]
  withDetails?: boolean
}

export const VatRateSelect = ({
  locale: providedLocale,
  onSelect,
  region,
  vatRates,
  withDetails,
  ...selectProps
}: VatRateSelectProps) => {
  const { t } = useTranslation()
  const locale: LocaleIdentifier = providedLocale || getCurrentLocale()

  const items = useMemo(
    () =>
      getItems({
        locale,
        region,
        t,
        vatRates,
        withDetails,
      }),
    [withDetails, locale, region, t, vatRates],
  )

  const getDisplayValue = useCallback(
    (item: NavItem<VatRateItemValue>) => getDisplayedVatRate({ vatRate: item.value, locale }),
    [locale],
  )

  const handleSelect = useCallback(
    (id?: string, value?: VatRateItemValue) => {
      onSelect?.(id, value)
    },
    [onSelect],
  )

  return (
    <Select
      {...selectProps}
      dropdownItemRender={(props) => <NavListItemWithBlockDescription {...props} />}
      getDisplayValue={getDisplayValue}
      items={items}
      onSelect={handleSelect}
    />
  )
}
